import { useCallback, useEffect, useMemo } from "react"
import { useNavigate } from "react-router"
import { useTranslation } from "react-i18next"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { cva } from "class-variance-authority"
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  Input,
  Label,
} from "@repo/ui"
import { Spinner } from "@phosphor-icons/react"
import { useStep, UseStepActions } from "@/lib/hooks/useStep"
import { CHOSABLE_ROLES } from "./constant"
import { WizardSuccessScreen } from "@/components/shared/WizardSuccessScreen"
import { LogoExtended } from "../layouts/public-layout"
import { useUpdateUserInfoMutation } from "@/feature/settings/api"

type StepsComponents = UseStepActions

const schema = z.object({
  name: z.string(),
  role: z.enum(CHOSABLE_ROLES),
})

type FormValues = z.infer<typeof schema>

const resolver = zodResolver(schema)

const buttonVariant = cva(`rounded-full`, {
  variants: {
    state: {
      selected: `hover:border-[#99B6FF] hover:bg-[#CCDBFF] hover:text-primary border-[#99B6FF] bg-[#CCDBFF] text-primary`,
      unselected: `hover:border-[#99B6FF] hover:bg-[#CCDBFF] hover:text-primary`,
    },
  },
  defaultVariants: {
    state: `unselected`,
  },
})

const GetStarted = (props: StepsComponents) => {
  const { t } = useTranslation(`wizard`)
  const {
    mutate: updateUserBusinessInfo,
    isSuccess,
    isPending,
  } = useUpdateUserInfoMutation()
  const form = useForm<FormValues>({
    resolver,
    defaultValues: {
      name: ``,
      role: undefined,
    },
  })

  const onSubmit = useCallback(
    (values: FormValues) => {
      updateUserBusinessInfo({
        ...values,
        status: `onboarded`,
      })
    },
    [updateUserBusinessInfo]
  )

  useEffect(() => {
    if (isSuccess) {
      props.goToNextStep()
    }
  }, [isSuccess, props])

  return (
    <div className="flex flex-col items-center justify-between h-full">
      <LogoExtended />
      <div className="flex flex-col items-center justify-center gap-5 h-full">
        <div className="max-w-md">
          <h1 className="text-4xl font-medium">
            {t(`sign_up.screens.profile.title`)}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t(`sign_up.screens.profile.description`)}
          </p>
          <Form {...form}>
            <form className="mt-8" onSubmit={form.handleSubmit(onSubmit)}>
              <FormField
                name="name"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-primary">
                      {t(`sign_up.screens.profile.inputs.name.label`)}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        autoComplete="additional-name"
                        placeholder={t(
                          `sign_up.screens.profile.inputs.name.placeholder`
                        )}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <div className="flex flex-col gap-4 mt-8">
                <Label className="text-primary">
                  {t(`sign_up.screens.profile.inputs.role.label`)}
                </Label>
                <div className="flex gap-2 flex-wrap">
                  {CHOSABLE_ROLES.map((role) => (
                    <Button
                      key={role}
                      type="button"
                      onClick={() =>
                        form.setValue(`role`, role, { shouldValidate: true })
                      }
                      className={buttonVariant({
                        state:
                          form.watch(`role`) === role
                            ? `selected`
                            : `unselected`,
                      })}
                      variant={`outline`}
                    >
                      {role}
                    </Button>
                  ))}
                </div>
              </div>
              <Button
                disabled={
                  !form.formState.isValid || isPending || !form.watch(`role`)
                }
                type="submit"
                className="mt-10"
                size="lg"
              >
                {isPending ? (
                  <Spinner className="w-4 h-4 animate-spin" size={20} />
                ) : (
                  t(`sign_up.screens.profile.button`)
                )}
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </div>
  )
}

export const Wizard = () => {
  const { t } = useTranslation(`wizard`)
  const [current, utils] = useStep(2)
  const navigate = useNavigate()
  const goToDashboard = useCallback(() => {
    navigate(`/`)
  }, [navigate])
  const lastItemUtils = useMemo(
    () => ({ ...utils, goToNextStep: goToDashboard }),
    [goToDashboard, utils]
  )
  return (
    <div className="bg-white w-full p-8 h-full">
      {current === 1 && <GetStarted {...utils} />}
      {current === 2 && (
        <WizardSuccessScreen actions={lastItemUtils}>
          <div>
            <h1 className="text-4xl text-center font-medium">
              {t(`sign_up.screens.success.title`)}
            </h1>
            <p className="text-muted-foreground mt-1 text-center">
              {t(`sign_up.screens.success.description`)}
            </p>
          </div>
        </WizardSuccessScreen>
      )}
    </div>
  )
}
